/* ============================================================
 * AYU Registrierung – Zwischen /funnel und /video
 * Sammelt: Vorname, Familienname, Telefon, E-Mail → CRM
 * Design: Warm ivory + olive/gold, konsistent mit AYU
 * ============================================================ */
import { useState, useEffect, useRef } from "react";
import { IMAGES } from "@/lib/constants";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/contexts/I18nContext";
import {
  User, Phone, Mail, ArrowRight, Shield, CheckCircle, Leaf,
  Loader2, LogIn, AlertCircle,
} from "lucide-react";

/* ── Phone number formatting & validation ── */
function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/[^\d+]/g, "");

  if (cleaned.startsWith("+49")) {
    const digits = cleaned.slice(3);
    if (digits.length <= 3) return `+49 ${digits}`;
    if (digits.length <= 6) return `+49 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }

  if (cleaned.startsWith("0")) {
    const digits = cleaned.slice(1);
    if (digits.length <= 3) return `+49 ${digits}`;
    if (digits.length <= 6) return `+49 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }

  if (cleaned.startsWith("+")) return cleaned;

  if (cleaned.length > 0) {
    const digits = cleaned;
    if (digits.length <= 3) return `+49 ${digits}`;
    if (digits.length <= 6) return `+49 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }

  return value;
}

function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+")) return false;
  const digits = cleaned.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

function isValidName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length < 2 || trimmed.length > 50) return false;
  // Letters (including German umlauts), spaces, hyphens, apostrophes only
  return /^[a-zA-ZäöüÄÖÜß][a-zA-ZäöüÄÖÜß\s\-']*$/.test(trimmed);
}

function isValidEmail(email: string): boolean {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return false;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) return false;
  const [, domain] = trimmed.split("@");
  const parts = domain.split(".");
  // Domain must have TLD ≥ 2 chars and no empty segments
  return parts.length >= 2 && parts.every(p => p.length > 0) && parts[parts.length - 1].length >= 2;
}

export default function Registrierung() {
  const { t } = useI18n();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    general?: string;
  }>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const [utmParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get("utm_source") || undefined,
      utmCampaign: params.get("utm_campaign") || undefined,
      utmMedium: params.get("utm_medium") || undefined,
      utmContent: params.get("utm_content") || undefined,
    };
  });

  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if user is already a full app client (logged in)
  const clientMeQuery = trpc.clientAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const registerMutation = trpc.leads.register.useMutation({
    onSuccess: (data) => {
      if (data.alreadyRegistered) {
        setAlreadyRegistered(true);
      } else {
        // Meta Pixel: Lead-Event bei erfolgreicher Erstregistrierung
        if (typeof window !== "undefined" && (window as any).fbq) {
          (window as any).fbq("track", "Lead", {
            content_name: "AYU Registrierung",
            content_category: "Funnel",
          });
        }
      }
      setSubmitted(true);
      redirectTimerRef.current = setTimeout(() => {
        window.location.href = "/video";
      }, data.alreadyRegistered ? 3000 : 2000);
    },
    onError: (err) => {
      setSubmitting(false);
      const msg = err.message || "";

      // Map server error messages to specific fields
      if (msg.toLowerCase().includes("e-mail") || msg.toLowerCase().includes("email")) {
        setErrors({ email: msg });
      } else if (msg.toLowerCase().includes("telefon") || msg.toLowerCase().includes("phone")) {
        setErrors({ phone: msg });
      } else if (msg.toLowerCase().includes("vorname")) {
        setErrors({ firstName: msg });
      } else if (msg.toLowerCase().includes("familienname") || msg.toLowerCase().includes("nachname")) {
        setErrors({ lastName: msg });
      } else {
        setErrors({ general: msg || t("registrierung.errorGeneral") });
      }
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  // Auto-redirect if already logged in as a client
  useEffect(() => {
    if (!clientMeQuery.isLoading && clientMeQuery.data) {
      redirectTimerRef.current = setTimeout(() => {
        window.location.href = "/app";
      }, 2500);
    }
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, [clientMeQuery.isLoading, clientMeQuery.data]);

  function handlePhoneChange(value: string) {
    const raw = value.replace(/[^\d+]/g, "");
    setPhone(raw.length > 0 ? formatPhoneNumber(value) : value);
    setErrors(prev => ({ ...prev, phone: undefined, general: undefined }));
  }

  function validate(): boolean {
    const newErrors: typeof errors = {};

    if (!firstName.trim()) {
      newErrors.firstName = t("registrierung.errorFirstNameRequired");
    } else if (firstName.trim().length < 2) {
      newErrors.firstName = t("registrierung.errorFirstNameMinLength");
    } else if (!isValidName(firstName)) {
      newErrors.firstName = t("registrierung.errorFirstNameInvalid");
    }

    if (!lastName.trim()) {
      newErrors.lastName = t("registrierung.errorLastNameRequired");
    } else if (lastName.trim().length < 2) {
      newErrors.lastName = t("registrierung.errorLastNameMinLength");
    } else if (!isValidName(lastName)) {
      newErrors.lastName = t("registrierung.errorLastNameInvalid");
    }

    if (!phone.trim()) {
      newErrors.phone = t("registrierung.errorPhoneRequired");
    } else if (!isValidPhone(phone.trim())) {
      newErrors.phone = t("registrierung.errorPhoneInvalid");
    }

    if (!email.trim()) {
      newErrors.email = t("registrierung.errorEmailRequired");
    } else if (!isValidEmail(email)) {
      newErrors.email = t("registrierung.errorEmailInvalid");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    registerMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      email: email.trim().toLowerCase(),
      ...utmParams,
    });
  }

  const isClientLoading = clientMeQuery.isLoading;
  const isAlreadyLoggedIn = !clientMeQuery.isLoading && !!clientMeQuery.data;
  const loggedInClient = clientMeQuery.data;

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: "oklch(0.97 0.005 80)" }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <img src={IMAGES.mandala} alt="" className="w-full h-full object-cover" />
      </div>

      {/* Navigation */}
      <nav className="relative z-20 py-5">
        <div className="container flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU" className="h-10 w-auto" />
          </a>
          <a
            href="/funnel"
            className="text-sm text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors"
            style={{ fontFamily: "var(--font-body)" }}
          >
            {t("registrierung.navBack")}
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container relative z-10 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-[calc(100vh-100px)]">
          {/* Left: Messaging */}
          <div className="pt-8 lg:pt-0">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-[oklch(0.52_0.09_128/0.3)] bg-[oklch(0.52_0.09_128/0.05)]">
              <div className="w-2 h-2 rounded-full bg-[oklch(0.52_0.09_128)] animate-pulse" />
              <span
                className="text-sm font-medium text-[oklch(0.52_0.09_128)]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("registrierung.stepIndicator")}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] leading-[1.15] mb-6">
              {t("registrierung.heroTitle1")}{" "}
              <span className="italic text-[oklch(0.52_0.09_128)]">
                {t("registrierung.heroTitle2")}
              </span>{" "}
              {t("registrierung.heroTitle3")}
            </h1>

            <p
              className="text-lg text-[oklch(0.45_0.02_80)] leading-relaxed mb-10 max-w-lg"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("registrierung.heroDesc")}
            </p>

            <div className="space-y-4">
              {[
                { icon: Shield, text: t("registrierung.trustBadge1") },
                { icon: Leaf, text: t("registrierung.trustBadge2") },
                { icon: CheckCircle, text: t("registrierung.trustBadge3") },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[oklch(0.52_0.09_128/0.08)] flex items-center justify-center flex-shrink-0">
                    <badge.icon size={16} className="text-[oklch(0.52_0.09_128)]" />
                  </div>
                  <span
                    className="text-[oklch(0.4_0.02_80)] text-[15px]"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {badge.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form / Status card */}
          <div className="flex justify-center lg:justify-end">

            {/* Checking auth state */}
            {isClientLoading ? (
              <div className="w-full max-w-lg p-10 rounded-3xl bg-white shadow-xl border border-[oklch(0.9_0.01_80)] flex items-center justify-center min-h-[300px]">
                <Loader2 size={32} className="animate-spin text-[oklch(0.52_0.09_128)]" />
              </div>

            /* Already logged in as app client → redirect to /app */
            ) : isAlreadyLoggedIn ? (
              <div className="w-full max-w-lg p-10 rounded-3xl bg-white shadow-xl border border-[oklch(0.52_0.09_128/0.15)] text-center">
                <div className="w-20 h-20 rounded-full bg-[oklch(0.52_0.09_128/0.1)] flex items-center justify-center mx-auto mb-6">
                  <LogIn size={40} className="text-[oklch(0.52_0.09_128)]" />
                </div>
                <h2 className="text-2xl font-bold text-[oklch(0.2_0.02_80)] mb-3">
                  {t("registrierung.alreadyLoggedInTitle")}
                </h2>
                <p
                  className="text-[oklch(0.45_0.02_80)] mb-4"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {t("registrierung.alreadyLoggedInDesc")}
                  {loggedInClient?.firstName ? `, ${loggedInClient.firstName}` : ""}{t("registrierung.alreadyLoggedInDescSuffix")}
                </p>
                <div className="flex items-center justify-center gap-2 text-[oklch(0.52_0.09_128)] mb-5">
                  <Loader2 size={18} className="animate-spin" />
                  <span className="text-sm" style={{ fontFamily: "var(--font-body)" }}>
                    {t("registrierung.redirectingToApp")}
                  </span>
                </div>
                <a
                  href="/app"
                  className="inline-block text-sm font-medium text-[oklch(0.52_0.09_128)] underline underline-offset-2"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {t("registrierung.goToApp")}
                </a>
              </div>

            /* Form submitted → success state */
            ) : submitted ? (
              <div className="w-full max-w-lg p-10 rounded-3xl bg-white shadow-xl border border-[oklch(0.52_0.09_128/0.15)] text-center">
                <div className="w-20 h-20 rounded-full bg-[oklch(0.52_0.09_128/0.1)] flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-[oklch(0.52_0.09_128)]" />
                </div>
                {alreadyRegistered ? (
                  <>
                    <h2 className="text-2xl font-bold text-[oklch(0.2_0.02_80)] mb-3">
                      {t("registrierung.alreadyRegisteredTitle")}
                    </h2>
                    <p
                      className="text-[oklch(0.45_0.02_80)] mb-4"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {t("registrierung.alreadyRegisteredDesc")}
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-[oklch(0.2_0.02_80)] mb-3">
                      {t("registrierung.successTitle")}
                    </h2>
                    <p
                      className="text-[oklch(0.45_0.02_80)] mb-4"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {t("registrierung.successDesc")}
                    </p>
                  </>
                )}
                <div className="flex items-center justify-center gap-2 text-[oklch(0.52_0.09_128)]">
                  <Loader2 size={18} className="animate-spin" />
                  <span className="text-sm" style={{ fontFamily: "var(--font-body)" }}>
                    {t("registrierung.redirecting")}
                  </span>
                </div>
              </div>

            /* Registration form */
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="w-full max-w-lg p-6 sm:p-10 rounded-3xl bg-white shadow-xl border border-[oklch(0.9_0.01_80)]"
              >
                <h2 className="text-2xl font-bold text-[oklch(0.2_0.02_80)] mb-2">
                  {t("registrierung.formTitle")}
                </h2>
                <p
                  className="text-[oklch(0.5_0.02_80)] text-sm mb-8"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {t("registrierung.formSubtitle")}
                </p>

                {/* General error banner */}
                {errors.general && (
                  <div className="flex items-start gap-3 mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
                    <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <p
                      className="text-red-600 text-sm leading-relaxed"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {errors.general}
                    </p>
                  </div>
                )}

                {/* Vorname */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium text-[oklch(0.3_0.02_80)] mb-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {t("registrierung.labelFirstName")}
                  </label>
                  <div className="relative">
                    <User
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[oklch(0.6_0.02_80)]"
                    />
                    <input
                      type="text"
                      value={firstName}
                      autoComplete="given-name"
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setErrors(prev => ({ ...prev, firstName: undefined, general: undefined }));
                      }}
                      placeholder={t("registrierung.placeholderFirstName")}
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-[oklch(0.2_0.02_80)] placeholder:text-[oklch(0.65_0.02_80)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[oklch(0.52_0.09_128/0.3)] focus:border-[oklch(0.52_0.09_128)] ${
                        errors.firstName
                          ? "border-red-400 bg-red-50/50"
                          : "border-[oklch(0.88_0.02_80)] bg-[oklch(0.98_0.005_80)]"
                      }`}
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  {errors.firstName && (
                    <p
                      className="text-red-500 text-xs mt-1.5 flex items-center gap-1"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      <AlertCircle size={12} className="flex-shrink-0" />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                {/* Familienname */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium text-[oklch(0.3_0.02_80)] mb-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {t("registrierung.labelLastName")}
                  </label>
                  <div className="relative">
                    <User
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[oklch(0.6_0.02_80)]"
                    />
                    <input
                      type="text"
                      value={lastName}
                      autoComplete="family-name"
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setErrors(prev => ({ ...prev, lastName: undefined, general: undefined }));
                      }}
                      placeholder={t("registrierung.placeholderLastName")}
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-[oklch(0.2_0.02_80)] placeholder:text-[oklch(0.65_0.02_80)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[oklch(0.52_0.09_128/0.3)] focus:border-[oklch(0.52_0.09_128)] ${
                        errors.lastName
                          ? "border-red-400 bg-red-50/50"
                          : "border-[oklch(0.88_0.02_80)] bg-[oklch(0.98_0.005_80)]"
                      }`}
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  {errors.lastName && (
                    <p
                      className="text-red-500 text-xs mt-1.5 flex items-center gap-1"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      <AlertCircle size={12} className="flex-shrink-0" />
                      {errors.lastName}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium text-[oklch(0.3_0.02_80)] mb-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {t("registrierung.labelPhone")}
                  </label>
                  <div className="relative">
                    <Phone
                      size={18}
                      className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-[oklch(0.6_0.02_80)]"
                    />
                    <input
                      type="tel"
                      value={phone}
                      autoComplete="tel"
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder={t("registrierung.placeholderPhone")}
                      className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3.5 rounded-xl border text-sm sm:text-base text-[oklch(0.2_0.02_80)] placeholder:text-[oklch(0.65_0.02_80)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[oklch(0.52_0.09_128/0.3)] focus:border-[oklch(0.52_0.09_128)] ${
                        errors.phone
                          ? "border-red-400 bg-red-50/50"
                          : "border-[oklch(0.88_0.02_80)] bg-[oklch(0.98_0.005_80)]"
                      }`}
                      style={{ fontFamily: "var(--font-body)", letterSpacing: "0.02em" }}
                    />
                  </div>
                  {errors.phone && (
                    <p
                      className="text-red-500 text-xs mt-1.5 flex items-center gap-1"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      <AlertCircle size={12} className="flex-shrink-0" />
                      {errors.phone}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-8">
                  <label
                    className="block text-sm font-medium text-[oklch(0.3_0.02_80)] mb-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {t("registrierung.labelEmail")}
                  </label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[oklch(0.6_0.02_80)]"
                    />
                    <input
                      type="email"
                      value={email}
                      autoComplete="email"
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors(prev => ({ ...prev, email: undefined, general: undefined }));
                      }}
                      placeholder={t("registrierung.placeholderEmail")}
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-[oklch(0.2_0.02_80)] placeholder:text-[oklch(0.65_0.02_80)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[oklch(0.52_0.09_128/0.3)] focus:border-[oklch(0.52_0.09_128)] ${
                        errors.email
                          ? "border-red-400 bg-red-50/50"
                          : "border-[oklch(0.88_0.02_80)] bg-[oklch(0.98_0.005_80)]"
                      }`}
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  {errors.email && (
                    <p
                      className="text-red-500 text-xs mt-1.5 flex items-center gap-1"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      <AlertCircle size={12} className="flex-shrink-0" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{
                    background: "oklch(0.52 0.09 128)",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t("registrierung.submitLoading")}
                    </>
                  ) : (
                    <>
                      {t("registrierung.submitButton")} <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <p
                  className="text-xs text-[oklch(0.6_0.02_80)] text-center mt-5 leading-relaxed"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {t("registrierung.privacyText")}{" "}
                  <a
                    href="/datenschutz"
                    className="text-[oklch(0.52_0.09_128)] hover:underline"
                  >
                    {t("registrierung.privacyLink")}
                  </a>{" "}
                  {t("registrierung.privacyAfter")}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-6 border-t border-[oklch(0.9_0.01_80)]">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-3">
          <span
            className="text-xs text-[oklch(0.5_0.02_80)]"
            style={{ fontFamily: "var(--font-body)" }}
          >
            &copy; {new Date().getFullYear()} AYU Healthtech LIMITED
          </span>
          <div className="flex items-center gap-6">
            <a
              href="/impressum"
              className="text-xs text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("registrierung.footerImpressum")}
            </a>
            <a
              href="/datenschutz"
              className="text-xs text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("registrierung.footerDatenschutz")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
